import { useQuery } from "@tanstack/react-query";

import { api } from "@/api/client";
import type {
  PhaseReference,
  PoolPhase,
  PoolPhaseRef,
  ReplicaPhase,
  ReplicaPhaseRef,
} from "@/api/types";

/**
 * Shared React-Query handle for `GET /reference/phases`.
 *
 * The catalogue is process-static on the controller (built from
 * `POOL_PHASE_DESCRIPTIONS` / `REPLICA_PHASE_DESCRIPTIONS`) so we
 * cache it aggressively — a deploy is the only thing that changes
 * it, and the page reload that comes with a deploy invalidates the
 * cache for free.
 */
export function usePhaseReference() {
  return useQuery<PhaseReference>({
    queryKey: ["reference", "phases"],
    queryFn: () => api.get<PhaseReference>("/reference/phases"),
    staleTime: 1000 * 60 * 30,
    gcTime: 1000 * 60 * 60,
  });
}

/**
 * Lookup helper used by {@link usePoolPhaseRef} / {@link useReplicaPhaseRef}.
 * Falls back to a synthesised descriptor so the UI doesn't crash if
 * the controller introduces a new phase before the WebUI bundle
 * catches up.
 */
function fallbackPoolPhaseRef(slug: PoolPhase): PoolPhaseRef {
  return {
    slug,
    label: slug.replace(/_/g, " "),
    description: "Unknown phase. The controller may be newer than this UI.",
    tone: "neutral",
    auto_recovers: false,
    next_phases: [],
    next_action_label: "",
    countdown_kind: "tick_boundary",
    expected_duration_seconds: null,
  };
}

function fallbackReplicaPhaseRef(slug: ReplicaPhase): ReplicaPhaseRef {
  return {
    slug,
    label: slug.replace(/_/g, " "),
    description: "Unknown phase. The controller may be newer than this UI.",
    tone: "neutral",
    progress_index: null,
    progress_total: 6,
    is_terminal: false,
  };
}

export function usePoolPhaseRef(slug: PoolPhase | undefined | null): PoolPhaseRef | null {
  const ref = usePhaseReference();
  if (!slug) return null;
  const found = ref.data?.pool_phases.find((p) => p.slug === slug);
  return found ?? fallbackPoolPhaseRef(slug);
}

export function useReplicaPhaseRef(
  slug: ReplicaPhase | undefined | null,
): ReplicaPhaseRef | null {
  const ref = usePhaseReference();
  if (!slug) return null;
  const found = ref.data?.replica_phases.find((p) => p.slug === slug);
  return found ?? fallbackReplicaPhaseRef(slug);
}
